import { FunctionComponent, useState } from 'react';
import HeaderSection from '../../components/sections/HeaderSection/HeaderSection';
import RSVP from './_parts/RSVP/RSVP';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import Sorted from './_parts/Sorted/Sorted';
import DevTools from '../../../../components/devTools/DevToolStateToggler/DevToolStateToggler';
import MetaTags from '../../../../components/seo/MetaTags/MetaTags';
import CompleteProfile from './_parts/CompleteProfile/CompleteProfile';
import AfterDinner from './_parts/AfterDinner/AfterDinner';
import Empty from './_parts/Empty/Empty';
import PopUp from '../../../../components/popups/PopUp/PopUp';
import { createPortal } from 'react-dom';
import styles from './UpcomingEventView.module.scss';
import MainButton from '../../../../components/buttons/MainButton/MainButton';
import classNames from 'classnames';
import GroupWithGap from '../../../../components/lists/GroupWithGap/GroupWithGap';
import { openModal } from '../../../../redux/slices/modal/slice';
import { CLIENT_SUPPORT_CONTENT } from '../../data/clientSupport';

type ViewState = 'RSVP' | 'Sorted' | 'CompleteProfile' | 'AfterDinner' | 'Empty' | 'FacilitatorPopUpTest';

interface UpcomingEventViewProps {}

const UpcomingEventView: FunctionComponent<UpcomingEventViewProps> = () => {
  const [viewState, setViewState] = useState<ViewState>('RSVP');
  const [rsvpModalActive, setRsvpModalActive] = useState<boolean>(false);
  const [facilitatorModalActive, setFacilitatorModalActive] = useState<boolean>(false);
  const [isRsvp, setIsRsvp] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const handleOpenLoginModal = () => {
    dispatch(openModal(CLIENT_SUPPORT_CONTENT.modalId));
  };

  // DEV TOOLS
  const viewStates = [
    { label: 'RSVP', value: 'RSVP' },
    { label: 'Sorted', value: 'Sorted' },
    { label: 'Complete Profile', value: 'CompleteProfile' },
    { label: 'After Dinner', value: 'AfterDinner' },
    { label: 'Empty', value: 'Empty' },
    { label: 'Facilitator modal', value: 'FacilitatorPopUpTest' },
  ];

  const handleViewStateChange = (newState: string) => {
    setViewState(newState as ViewState);
  };
  // DEV TOOLS END

  const renderContent = () => {
    switch (viewState) {
      case 'RSVP':
        return <RSVP isRsvp={isRsvp} setIsRsvp={setIsRsvp} setRsvpModalActive={setRsvpModalActive} />;
      case 'Sorted':
        return <Sorted isRsvp={true} isFacilitator={true} setRsvpModalActive={setRsvpModalActive} />;
      case 'CompleteProfile':
        return <CompleteProfile />;
      case 'AfterDinner':
        return <AfterDinner />;
      case 'Empty':
        return <Empty />;
      case 'FacilitatorPopUpTest':
        if (facilitatorModalActive === false && viewState === 'FacilitatorPopUpTest') setFacilitatorModalActive(true);
        return <></>;

      default:
        return <div>Default Content</div>;
    }
  };

  return (
    <>
      <MetaTags title="Constellation Dinners - Upcoming Event" hideMainTitle={true} />
      {renderContent()}
      <DevTools
        title="View State"
        states={viewStates}
        initialState={viewState}
        onChange={handleViewStateChange}
        position="top-left"
      />

      {createPortal(
        <PopUp
          className={styles['common-pop-up']}
          title="See You at Dinner!"
          active={rsvpModalActive}
          onPopUpStateChange={setRsvpModalActive}
          footer={
            <GroupWithGap
              className={styles['common-pop-up__button-group']}
              items={[
                <MainButton
                  sizeType="medium"
                  visualType="white"
                  onClick={() => {
                    setRsvpModalActive(false);
                  }}
                >
                  Close
                </MainButton>,
                <MainButton
                  sizeType="medium"
                  onClick={() => {
                    setRsvpModalActive(false);
                    handleOpenLoginModal();
                  }}
                >
                  Contact Karla
                </MainButton>,
              ]}
            />
          }
        >
          <p className={classNames(styles['common-pop-up__text-1'], 'text text--body-2')}>
            <b>Your RSVP is confirmed!</b> Get ready for great conversations and delicious food. Check the event details
            anytime in the app, and if you need anything, Karla's here to help. We can't wait to see you there!
          </p>
        </PopUp>,
        document.getElementById('modal') as HTMLElement
      )}

      {createPortal(
        <PopUp
          className={styles['common-pop-up']}
          title="Are you open to facilitating this dinner?"
          active={facilitatorModalActive}
          onPopUpStateChange={setFacilitatorModalActive}
          footer={
            <GroupWithGap
              className={styles['common-pop-up__button-group']}
              items={[
                <MainButton
                  sizeType="medium"
                  visualType="white"
                  onClick={() => {
                    setRsvpModalActive(false);
                  }}
                >
                  No
                </MainButton>,
                <MainButton
                  sizeType="medium"
                  onClick={() => {
                    setRsvpModalActive(false);
                    handleOpenLoginModal();
                  }}
                >
                  Yes, make me a facilitator
                </MainButton>,
              ]}
            />
          }
        >
          <p className={classNames(styles['common-pop-up__text-1'], 'text text--body-2')}>
            Want to take a more active role? Facilitators help ensure a smooth dinner experience and get recognized as
            key contributors to our community. It's a great way to practice leadership while enjoying the evening.
          </p>
        </PopUp>,
        document.getElementById('modal') as HTMLElement
      )}
    </>
  );
};

export default UpcomingEventView;
